import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data", "previousData", "refetch", "stopPolling", "error"];
import { useQuery } from '@apollo/client';
import { FETCH_ASSOCIATED_OBJECTS_CARD_ASSOCIATIONS } from '../queries';
import memoize from 'transmute/memoize';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { SUCCESSFUL_DATA_POLL_TIME, Metrics } from '../helpers/Metrics';
import { PRIMARY_ASSOCIATION_BY_OBJECT_TYPE_ID_INVERSE_ID, UNLABELED_ASSOCIATION_BY_OBJECT_TYPE_ID_INVERSE_ID } from '../chicklets/associations/AssociationLabelConstants';
import { getRequiredPropertyNames } from '../constants/RequiredPropertyNames';
const DEFAULT_SORT = [Object.freeze({
  property: 'hs_object_id',
  order: 'DESC'
})];
export const POLL_INTERVAL = 500;
export const UNKNOWN_ACTION = 'UNKNOWN';
const ASSOCIATION_CATEGORIES = Object.freeze({
  HUBSPOT_DEFINED: '0',
  USER_DEFINED: '1',
  INTEGRATOR_DEFINED: '2'
});
function getCompleteAssociationTypeId({
  associationCategory,
  associationTypeId,
  fromObjectTypeId,
  toObjectTypeId,
  idMap
}) {
  const category = associationCategory ? ASSOCIATION_CATEGORIES[associationCategory] : null;
  if (!category || !associationTypeId) {
    const hardcodedHubspotDefinedAssociationTypeId = idMap[fromObjectTypeId] && idMap[fromObjectTypeId][toObjectTypeId];
    return hardcodedHubspotDefinedAssociationTypeId && `0-${hardcodedHubspotDefinedAssociationTypeId}` || null;
  }
  return `${category}-${associationTypeId}`;
}
export const getAssociationsFilter = memoize((completeAssociationTypeId, fromObjectId) => {
  const filter = {
    property: `associations.${completeAssociationTypeId}`,
    operator: 'EQ',
    value: `${fromObjectId}`
  };
  return filter;
});
export function getImpliedUnlabeledAssociationDetails({
  associationTypes,
  unlabeledAssociationType
}) {
  const impliedUnlabeledAssociationCategory = unlabeledAssociationType && unlabeledAssociationType.associationCategory || associationTypes[0] && associationTypes[0].associationCategory || null;
  const impliedUnlabeledAssociationTypeId = unlabeledAssociationType && unlabeledAssociationType.associationTypeId || associationTypes[0] && associationTypes[0].associationTypeId || null;
  const impliedUnlabeledInverseAssociationTypeId = unlabeledAssociationType && unlabeledAssociationType.inverseAssociationTypeId || associationTypes[0] && associationTypes[0].inverseAssociationTypeId || null;
  return {
    impliedUnlabeledAssociationCategory,
    impliedUnlabeledAssociationTypeId,
    impliedUnlabeledInverseAssociationTypeId
  };
}
export function useFetchAssociatedObjectsCardAssociations({
  fromObjectId,
  impliedUnlabeledAssociationCategory,
  impliedUnlabeledInverseAssociationTypeId,
  fromObjectTypeId,
  primaryAssociationCategory,
  primaryInverseAssociationTypeId,
  propertyNames,
  toObjectTypeId
}) {
  const updateAssociationAction = useRef(undefined);
  const pollTime = useRef(0);
  const requiredPropertyNames = useMemo(() => {
    return getRequiredPropertyNames(toObjectTypeId, propertyNames);
  }, [propertyNames, toObjectTypeId]);
  const impliedUnlabeledCompleteAssociationTypeId = getCompleteAssociationTypeId({
    associationCategory: impliedUnlabeledAssociationCategory,
    associationTypeId: impliedUnlabeledInverseAssociationTypeId,
    fromObjectTypeId,
    idMap: UNLABELED_ASSOCIATION_BY_OBJECT_TYPE_ID_INVERSE_ID,
    toObjectTypeId
  });
  const primaryCompleteAssociationTypeId = getCompleteAssociationTypeId({
    associationCategory: primaryAssociationCategory,
    associationTypeId: primaryInverseAssociationTypeId || null,
    fromObjectTypeId,
    idMap: PRIMARY_ASSOCIATION_BY_OBJECT_TYPE_ID_INVERSE_ID,
    toObjectTypeId
  });
  const impliedUnlabeledAssociationFilterGroups = useMemo(() => {
    if (!impliedUnlabeledCompleteAssociationTypeId) {
      return [];
    }
    const associationFilter = getAssociationsFilter(impliedUnlabeledCompleteAssociationTypeId, fromObjectId);
    return [{
      filters: [associationFilter]
    }];
  }, [impliedUnlabeledCompleteAssociationTypeId, fromObjectId]);
  const primaryAssociationFilterGroups = useMemo(() => {
    if (!primaryCompleteAssociationTypeId) {
      return [];
    }
    const associationFilter = getAssociationsFilter(primaryCompleteAssociationTypeId, fromObjectId);
    return [{
      filters: [associationFilter]
    }];
  }, [primaryCompleteAssociationTypeId, fromObjectId]);
  const _useQuery = useQuery(FETCH_ASSOCIATED_OBJECTS_CARD_ASSOCIATIONS, {
      variables: {
        unlabeledAssociationFilterGroups: impliedUnlabeledAssociationFilterGroups,
        hasPrimaryAssociation: Boolean(primaryCompleteAssociationTypeId),
        offset: 0,
        pageSize: 5,
        primaryAssociationFilterGroups,
        sorts: DEFAULT_SORT,
        toObjectTypeId,
        propertyNames: requiredPropertyNames
      },
      nextFetchPolicy: 'cache-first',
      fetchPolicy: 'cache-and-network',
      skip: !impliedUnlabeledCompleteAssociationTypeId
    }),
    {
      data,
      previousData,
      refetch,
      stopPolling,
      error
    } = _useQuery,
    useQueryResult = _objectWithoutPropertiesLoose(_useQuery, _excluded);
  const handleRefetchAssociationsData = useCallback(async args => {
    const pageSize = data && data.impliedUnlabeledAssociationObjects.results.length > 5 ? data.impliedUnlabeledAssociationObjects.results.length : 5;
    return await refetch(Object.assign({
      pageSize
    }, args));
  }, [data, refetch]);
  const handleStopPolling = useCallback(() => {
    updateAssociationAction.current = undefined;
    pollTime.current = 0;
    stopPolling();
  }, [stopPolling]);
  useEffect(() => {
    const difOfTotal = Math.abs(data && previousData ? previousData.impliedUnlabeledAssociationObjects.total - data.impliedUnlabeledAssociationObjects.total : 0);
    const isPollingAndDataChanged = difOfTotal > 0;
    if (updateAssociationAction.current) {
      pollTime.current += POLL_INTERVAL;
    }
    if (!isPollingAndDataChanged) {
      return;
    }
    Metrics.histogram(SUCCESSFUL_DATA_POLL_TIME, {
      associationAction: updateAssociationAction.current || UNKNOWN_ACTION
    }).update(pollTime.current);
    // We can only remove 1 association at a time when removing so we can stop polling
    if (updateAssociationAction.current === 'REMOVE_ASSOCIATION') {
      handleStopPolling();
    }
  }, [data, handleStopPolling, previousData]);

  // Assign alias here for primaryAssociation since there is a type generation bug
  // https://hubspot.slack.com/archives/CATGF5TQS/p1707755380329999
  return Object.assign({
    impliedUnlabeledAssociationObjects: data && data.impliedUnlabeledAssociationObjects,
    onStopPolling: handleStopPolling,
    pollTime,
    primaryAssociationObject: data && data.crmObjectsSearch,
    refetch: handleRefetchAssociationsData,
    updateAssociationAction,
    associationsDataError: error
  }, useQueryResult);
}